import * as client from "../../../client";
import { SafeReportResponse } from "../Conversion";
import { blendedItvPerSqft } from "../sov/utils";

// The Post Bind "Description of Risk" is a fully auto-generated prose paragraph
// stitched from the enabled-address SOV data. Each clause is omitted when its
// underlying data is missing, so a sparse SOV still yields a clean sentence.

export const enabledAddressesFromReport = (
  report: SafeReportResponse
): client.RawAddress[] =>
  report.report_json.sovs
    .filter((sov) => sov.is_enabled)
    .flatMap((sov) => sov.addresses);

const parseNumeric = (val: string | null | undefined): number | null => {
  if (val == null) return null;
  const n = parseFloat(String(val).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const formatPercentInt = (fraction: number): string =>
  `${Math.round(fraction * 100)}%`;

// Sum TIV by a string dimension, returning groups sorted by TIV descending.
const sumTivBy = (
  addresses: client.RawAddress[],
  keyOf: (a: client.RawAddress) => string | null | undefined
): { key: string; tiv: number }[] => {
  const map = new Map<string, number>();
  for (const a of addresses) {
    const k = keyOf(a)?.trim();
    if (!k) continue;
    map.set(k, (map.get(k) ?? 0) + (a.tiv ?? 0));
  }
  return [...map.entries()]
    .map(([key, tiv]) => ({ key, tiv }))
    .sort((x, y) => y.tiv - x.tiv);
};

// Freeform SOV sprinkler values: treat affirmative codes / positive numbers as
// sprinklered; explicit negatives as not; unknown text is left out of the count.
const SPRINKLER_YES = new Set([
  "y",
  "yes",
  "true",
  "sprinklered",
  "fully sprinklered",
  "wet",
  "dry",
]);
const SPRINKLER_NO = new Set([
  "n",
  "no",
  "none",
  "false",
  "unsprinklered",
  "non-sprinklered",
  "not sprinklered",
  "0",
]);

const isSprinklered = (val: string | null | undefined): boolean => {
  if (!val) return false;
  const v = String(val).trim().toLowerCase();
  if (SPRINKLER_NO.has(v)) return false;
  if (SPRINKLER_YES.has(v)) return true;
  const n = parseNumeric(v);
  return n != null ? n > 0 : false;
};

// FEMA high-hazard flood zones begin with A or V.
const isHighHazardFlood = (val: string | null | undefined): boolean => {
  if (!val) return false;
  const c = String(val).trim().charAt(0).toUpperCase();
  return c === "A" || c === "V";
};

const sumTiv = (addresses: client.RawAddress[]): number =>
  addresses.reduce((acc, a) => acc + (a.tiv ?? 0), 0);

export const buildRiskDescription = (
  report: SafeReportResponse
): string | null => {
  const addresses = enabledAddressesFromReport(report);
  if (addresses.length === 0) return null;

  const totalTiv = sumTiv(addresses);
  const clauses: string[] = [];

  // Opener: occupancy + states (with top-3 by TIV) + location count.
  const occupancy = sumTivBy(addresses, (a) => a.occupancy_description)[0]?.key;
  const states = sumTivBy(addresses, (a) => a.state);
  const locationCount = addresses.length;

  let opener = "Risk is a schedule";
  if (occupancy) opener += ` of ${occupancy.toLowerCase()}`;
  if (states.length > 0) {
    opener += ` across ${states.length} ${
      states.length === 1 ? "state" : "states"
    }`;
    if (totalTiv > 0) {
      const top = states
        .slice(0, 3)
        .map((s) => `${s.key}-${formatPercentInt(s.tiv / totalTiv)}`);
      opener += ` (largest include ${top.join(", ")})`;
    }
  }
  opener += ` with ${locationCount} ${
    locationCount === 1 ? "location" : "locations"
  }`;
  clauses.push(opener);

  // Predominant construction by TIV.
  const construction = sumTivBy(addresses, (a) => a.construction_description);
  if (construction.length > 0 && totalTiv > 0) {
    const top = construction[0]!;
    clauses.push(
      `predominantly ${top.key.toLowerCase()} construction (${formatPercentInt(
        top.tiv / totalTiv
      )})`
    );
  }

  // Blended ITV ($/sq ft) — shares logic with the SOV tab's ITV column so the
  // two surfaces always agree.
  const itv = blendedItvPerSqft(addresses);
  if (itv != null) {
    clauses.push(`average ITV of $${Math.round(itv)}/sq ft`);
  }

  // Sprinklered share, over locations whose sprinkler status is known.
  const sprinklerKnown = addresses.filter(
    (a) => a.sprinkler != null && a.sprinkler !== ""
  );
  const sprinklerKnownTiv = sumTiv(sprinklerKnown);
  if (sprinklerKnownTiv > 0) {
    const sprinkleredTiv = sumTiv(
      sprinklerKnown.filter((a) => isSprinklered(a.sprinkler))
    );
    clauses.push(
      `${formatPercentInt(sprinkleredTiv / sprinklerKnownTiv)} sprinklered`
    );
  }

  // High-hazard flood share (FEMA A/V zones), over locations with a known zone.
  const floodKnown = addresses.filter(
    (a) => a.flood_zone != null && a.flood_zone !== ""
  );
  const floodKnownTiv = sumTiv(floodKnown);
  if (floodKnownTiv > 0) {
    const floodTiv = sumTiv(
      floodKnown.filter((a) => isHighHazardFlood(a.flood_zone))
    );
    if (floodTiv > 0) {
      clauses.push(
        `${formatPercentInt(floodTiv / floodKnownTiv)} in flood zones A/V`
      );
    }
  }

  return `${clauses.join("; ")}.`;
};
